import {
  isGroupedExportVariant,
  type DownloadSettingsModalVariant,
} from '@/app/(main)/client-detail/types/downloadSettingsModalVariant';
import type { UserExportSettingsInner } from '@/types/userExportSettingsApiTypes';
import { getSectionsForVariant } from '../consts/sectionsConfig';
import { DEFAULT_FORM_VALUES } from '../consts/defaultFormValues';
import type { ExportFormat, FormValues, SectionConfig } from '../types';
import { documentTypeToFormat } from './formatConverters';
import { comfortGranularAny, loyaltyGranularAny, passportGranularAny } from './granularChecks';
import { savedTravelPrefsCategoryMasterOn } from './travelPreferences';

function collectVisibleFormKeys(sections: SectionConfig[]): Set<keyof FormValues> {
  const keys = new Set<keyof FormValues>(['include_passport_files_as_separate_pdfs']);

  sections.forEach((section) => {
    keys.add(section.key);
    section.subItems?.forEach((sub) => {
      keys.add(sub.key);
    });
  });

  return keys;
}

/** Clears keys not shown in the current variant so UI, button state, and payload stay aligned. */
function stripFormValuesToVisibleSections(values: FormValues, sections: SectionConfig[]): FormValues {
  const visibleKeys = collectVisibleFormKeys(sections);
  const next = { ...values };

  (Object.keys(next) as (keyof FormValues)[]).forEach((key) => {
    if (!visibleKeys.has(key)) {
      next[key] = false;
    }
  });

  return next;
}

export function formStateFromExportSettings(settings: UserExportSettingsInner): {
  format: ExportFormat;
  values: FormValues;
} {
  return {
    format: documentTypeToFormat(settings.type),
    values: {
      ...DEFAULT_FORM_VALUES,
      include_basic_information: settings.include_basic_information.toggle,
      include_basic_information_first_name: settings.include_basic_information.first_name,
      include_basic_information_middle_name: settings.include_basic_information.middle_name,
      include_basic_information_last_name: settings.include_basic_information.last_name,
      include_basic_information_primary_email: settings.include_basic_information.primary_email,
      include_basic_information_date_of_birth: settings.include_basic_information.date_of_birth,
      include_basic_information_phone: settings.include_basic_information.phone,
      include_basic_information_gender: settings.include_basic_information.gender,
      include_basic_information_home_airports: settings.include_basic_information.home_airports,
      include_basic_information_age: settings.include_basic_information.age ?? false,
      include_basic_information_relationship_to_primary:
        settings.include_basic_information.relationship_to_primary ?? false,
      include_address: settings.include_address.toggle,
      include_address_mailing: settings.include_address.mailing,
      include_address_billing: settings.include_address.billing,
      group_view_preferences_allergies:
        settings.include_allergies_dietary_restrictions || settings.include_flight_preferences,
      group_view_comfort_related: comfortGranularAny({
        include_comfort_related_full_name: settings.include_comfort_related_full_name ?? false,
        include_comfort_related_height: settings.include_comfort_related_height ?? false,
        include_comfort_related_weight: settings.include_comfort_related_weight ?? false,
        include_comfort_related_details: settings.include_comfort_related_details ?? false,
      }),
      include_allergies_dietary_restrictions: settings.include_allergies_dietary_restrictions,
      include_loyalty_programs:
        (settings.include_loyalty_programs ?? false) ||
        loyaltyGranularAny({
          include_loyalty_program_full_name: settings.include_loyalty_program_full_name ?? false,
          include_loyalty_program_program: settings.include_loyalty_program_program ?? false,
          include_loyalty_program_number: settings.include_loyalty_program_number ?? false,
        }),
      include_loyalty_program_full_name: settings.include_loyalty_program_full_name ?? false,
      include_loyalty_program_program: settings.include_loyalty_program_program ?? false,
      include_loyalty_program_number: settings.include_loyalty_program_number ?? false,
      include_important_dates: settings.include_important_dates.toggle,
      include_important_dates_date_of_birth: settings.include_important_dates.date_of_birth,
      include_important_dates_confirmation_ceremony: settings.include_important_dates.confirmation_ceremony,
      include_important_travel_data: settings.include_important_travel_data.toggle,
      include_important_travel_data_global_entry_number: settings.include_important_travel_data.global_entry_number,
      include_important_travel_data_known_traveler_number: settings.include_important_travel_data.known_traveler_number,
      include_emergency_contact: settings.include_emergency_contact,
      include_flight_preferences: settings.include_flight_preferences,
      include_travel_preferences_by_category: savedTravelPrefsCategoryMasterOn(
        settings.include_travel_preferences_by_category
      ),
      include_travel_preferences_by_category_travel: settings.include_travel_preferences_by_category.travel,
      include_travel_preferences_by_category_lifestyle: settings.include_travel_preferences_by_category.lifestyle,
      include_travel_preferences_by_category_stay: settings.include_travel_preferences_by_category.stay,
      include_travel_preferences_by_category_personal: settings.include_travel_preferences_by_category.personal,
      include_related_contacts: settings.include_related_contacts,
      include_notes: settings.include_notes,
      include_tasks: settings.include_tasks,
      include_client_documents: settings.include_client_documents,
      include_passport_details:
        (settings.include_passport_details ?? false) ||
        passportGranularAny({
          include_passport_details_passport_number: settings.include_passport_details_passport_number ?? false,
          include_passport_details_document_date_of_birth:
            settings.include_passport_details_document_date_of_birth ?? false,
          include_passport_details_expiry_date: settings.include_passport_details_expiry_date ?? false,
        }),
      include_passport_details_passport_number: settings.include_passport_details_passport_number ?? false,
      include_passport_details_document_date_of_birth:
        settings.include_passport_details_document_date_of_birth ?? false,
      include_passport_details_expiry_date: settings.include_passport_details_expiry_date ?? false,
      include_comfort_related_full_name: settings.include_comfort_related_full_name ?? false,
      include_comfort_related_height: settings.include_comfort_related_height ?? false,
      include_comfort_related_weight: settings.include_comfort_related_weight ?? false,
      include_comfort_related_details: settings.include_comfort_related_details ?? false,
      include_passport_files_as_separate_pdfs: settings.include_passport_files_as_separate_pdfs,
    },
  };
}

export function getInitialFormState(
  initialExportSettings: UserExportSettingsInner,
  variant: DownloadSettingsModalVariant
): { format: ExportFormat; values: FormValues } {
  const initialBase = formStateFromExportSettings(initialExportSettings);
  const sections = getSectionsForVariant(variant);

  let values = initialBase.values;

  if (isGroupedExportVariant(variant)) {
    const nextValues: FormValues = {
      ...values,
      include_passport_details_document_date_of_birth: false,
    };

    values = {
      ...nextValues,
      include_passport_details: passportGranularAny(nextValues, { ignoreDocumentDob: true }),
    };
  }

  return {
    format: initialBase.format,
    values: stripFormValuesToVisibleSections(values, sections),
  };
}
